import {api} from "@/shared/api";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {formatCurrency, formatDate, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {FeatureModule, FeatureModulesLicensedUsersInfoResult, FeatureModulesPriceCalculationResult} from "@/shared/timr";
import {PricingChangeType} from "@/shared/timrTypes";

export interface LicensedUserDialogConfig {
	selectedFeatureModules: FeatureModule[];
	newUserCount?: number;
	allowedChange?: PricingChangeType;
}


const template = `
				<div id="licensedUsersDialog">
					<div class="flex-row">
						<div class="flex-column-30">
							${messages["pricing.licensedUsersDialog.currentLicensedUserCount.label"]}
						</div>
						<div class="flex-column-10"></div>
						<div data-current-licensed class="flex-column-30"></div>
						<div data-currently-used class="flex-column-30"></div>
					</div>
					<div id="numberOfUsersRow" class="flex-row mt-20 form-group">
						<div class="flex-column-30 pt-8">
							${messages["pricing.licensedUsersDialog.newLicensedUserCount.label"]}
						</div>
						<div class="flex-column-10"></div>
						<div class="flex-column-20">
							<div class="input-group">
								<div class="input-group-btn">
									<button data-user-count type="button" class="btn btn-default btn-icon" value="-1">
										<i class="far fa-minus"></i>
									</button>
								</div>
								<input data-user-count-input type="text" class="form-control" />
								<div class="input-group-btn">
									<button data-user-count type="button" class="btn btn-default btn-icon" value="1">
										<i class="far fa-plus"></i>
									</button>
								</div>
							</div>
						</div>
					</div>
					<div class="flex-row mt-20">
						<div data-user-difference-text class="flex-column-30">
							${messages["pricing.licensedUsersDialog.userDifference.added"]}
						</div>
						<div class="flex-column-10"></div>
						<h4 data-user-difference class="flex-column-60 m-0">0</h4>
					</div>
					<div data-upgrade-view class="flex-row h4">
						${messages["pricing.licensedUsersDialog.upgrade"]}
					</div>
					<div data-upgrade-view class="flex-row">
						<div data-upgrade-label class="flex-column-30"></div>
						<div class="flex-column-10"></div>
						<div data-upgrade-price class="flex-column-60"></div>
					</div>
					<div data-downgrade-view class="flex-row h4">
						${messages["pricing.licensedUsersDialog.downgrade"]}
					</div>
					<div data-downgrade-view class="flex-row">
						${messages["pricing.customize.downgrade.info"]}
					</div>
				</div>
			`;

export class LicensedUserDialog extends HTMLElement {
	private config: LicensedUserDialogConfig;
	private callback: (userCount: number) => void;

	private licensedUsersInfo: FeatureModulesLicensedUsersInfoResult;
	private submitBtn: JQuery;

	public static show(config: LicensedUserDialogConfig, callback: (userCount: number) => void) {
		const dialog = document.createElement("licensed-users-dialog") as LicensedUserDialog;
		dialog.config = config;
		dialog.callback = callback;
		dialog.show();
	}

	private connectedCallback() {
		this.innerHTML = template;
		this.loadFeatureModuleLicensedUsersInfo();
	}

	private show() {
		new BootstrapModal.Modal({
			title: "pricing.licensedUsersDialog.title",
			message: $(this),
			size: "modal-lg",
			destroyOnClose: true,
			buttons: [
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				},
				{
					text: "dialog.applySelection",
					css: "saveButton btn-primary",
					action: (m) => {
						this.callback(Number($("[data-user-count-input]").val()));
						m.close();
					}
				}
			]
		}).show();
	}

	private initDialogValues() {
		this.submitBtn = $(".saveButton");
		const upgradeView = $("[data-upgrade-view]");
		const downgradeView = $("[data-downgrade-view]");
		$("[data-current-licensed]").text(this.licensedUsersInfo.licensedUsers);
		$("[data-currently-used]").text(messages.withArguments("pricing.licensedUsersDialog.currentLicensedUserCount.inUse", this.licensedUsersInfo.activeUsers));
		$("[data-user-count-input]").val(this.config.newUserCount ?? this.licensedUsersInfo.licensedUsers);
		this.updateUpgradePrice(this.config.newUserCount ?? this.licensedUsersInfo.licensedUsers);
		if (this.config.newUserCount && this.config.newUserCount > this.licensedUsersInfo.licensedUsers) {
			this.updateUpgradeText(this.config.newUserCount);
			downgradeView.hide();
		} else if (this.config.newUserCount && this.config.newUserCount < this.licensedUsersInfo.licensedUsers) {
			this.updateUpgradeText(this.config.newUserCount);
			downgradeView.show();
			upgradeView.hide();
		} else {
			downgradeView.hide();
			upgradeView.hide();
		}

		this.toggleButtons(this.config.newUserCount ?? this.licensedUsersInfo.licensedUsers);
	}

	private initListeners() {
		$("[data-user-count]").click((e) => {
			const btn = $(e.target).closest(".btn");
			const value = Number(btn.val());
			const input = btn.closest(".input-group").find("input");
			const newValue = Math.max(1, Number(input.val()) + value);
			input.val(newValue);
			this.updateUpgradePrice(newValue);
			this.updateUpgradeText(newValue);
			this.updateErrorMessage(input, newValue);
		});
		$("[data-user-count-input]").change((e) => {
			const input = $(e.target);
			const newValue = Math.max(1, Number(input.val()));
			input.val(newValue);
			this.updateUpgradePrice(newValue);
			this.updateUpgradeText(newValue);
			this.updateErrorMessage(input, newValue);
		});
	}

	private updateUpgradePrice(users: number): void {
		// Prevent api call if upgrade is not allowed
		if (users >= this.licensedUsersInfo.licensedUsers && this.config.allowedChange !== PricingChangeType.DOWNGRADE) {
			const selectedModuleIds = this.config.selectedFeatureModules.map((m) => m.id);
			void api("pricing/calculateUpgrade.json")
				.get({
					months: 1,
					selectedModuleIds: selectedModuleIds,
					users: users,
					applicableVAT: 0
				})
				.done((data: FeatureModulesPriceCalculationResult) => {
					$("[data-upgrade-price]").text(formatCurrency(data.upgradeSummary.upgradeFees.userUpgradeFee));
					$("[data-upgrade-view]").toggle(data.upgradeSummary.upgradeFees.userUpgradeFee > 0);
					$("[data-downgrade-view]").hide();
				});
		} else {
			$("[data-upgrade-view]").hide();
			$("[data-downgrade-view]").show();
		}
		this.toggleButtons(users);
	}

	private updateUpgradeText(users: number): void {
		$("[data-upgrade-label]").text(messages.withArguments("pricing.licensedUsersDialog.upgrade.label",
			this.licensedUsersInfo.licensedUsers,
			users,
			formatDate(Temporal.Now.plainDateISO()),
			formatDate(parseISODate(this.licensedUsersInfo.validTo)))
		);
		const difference = users - this.licensedUsersInfo.licensedUsers;
		$("[data-user-difference-text]").text(messages[`pricing.licensedUsersDialog.userDifference.${difference >= 0 ? "added" : "removed"}`]);
		$("[data-user-difference]").text(String(difference));
	}

	private toggleButtons(users: number): void {
		if (users < this.licensedUsersInfo.licensedUsers) {
			Input.toggle(this.submitBtn, this.config.allowedChange !== PricingChangeType.UPGRADE);
			Input.enable(document.querySelector("[data-user-count][value='1']"));
		} else if (users > this.licensedUsersInfo.licensedUsers) {
			Input.toggle(this.submitBtn, this.config.allowedChange !== PricingChangeType.DOWNGRADE);
			Input.enable(document.querySelector("[data-user-count][value='-1']"));
		} else {
			Input.enable(this.submitBtn);
			Input.toggle(document.querySelector("[data-user-count][value='-1']"), this.config.allowedChange !== PricingChangeType.UPGRADE);
			Input.toggle(document.querySelector("[data-user-count][value='1']"), this.config.allowedChange !== PricingChangeType.DOWNGRADE);
		}
	}

	private updateErrorMessage(input: JQuery, users: number): void {
		const saveButton = $(".saveButton");
		Input.removeFeedback(input);
		Input.enable(saveButton);
		if (users < this.licensedUsersInfo.activeUsers) {
			Input.addFeedback(input, "has-error", messages.withArguments("pricing.licencedUsersDialog.tooManyUsers", this.licensedUsersInfo.activeUsers));
			Input.disable(saveButton);
		}
		if (users < this.licensedUsersInfo.licensedUsers && this.config.allowedChange === PricingChangeType.UPGRADE) {
			Input.addFeedback(input, "has-error", messages["pricing.customize.noUpgradeAndDowngrade.downgrade"]);
			Input.disable(saveButton);
		}
		if (users > this.licensedUsersInfo.licensedUsers && this.config.allowedChange === PricingChangeType.DOWNGRADE) {
			Input.addFeedback(input, "has-error", messages["pricing.customize.noUpgradeAndDowngrade.upgrade"]);
			Input.disable(saveButton);
		}
	}

	private loadFeatureModuleLicensedUsersInfo() {
		void api("pricing/getLicensedUsersInfo.json")
			.get()
			.done((data: FeatureModulesLicensedUsersInfoResult) => {
				this.licensedUsersInfo = data;
				this.initDialogValues();
				this.initListeners();
			});
	}
}
